/**
 * `src/hooks.client.ts` — the client error hook. DECISIONS D2 (Arabic-only UI).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  WHY THIS FILE EXISTS: THE VERSION GATE FAILED CORRECTLY AND SAID IT IN LATIN
 * ═══════════════════════════════════════════════════════════════════════════
 * `read/[surah]/+page.ts` lets `CorpusVersionError` propagate on purpose —
 * SPEC §3.2's renderer gate is `$lib/content` throwing and `read/+error.svelte`
 * catching, and no mushaf is drawn and no `ref` can be minted. That half worked.
 *
 * The message did not. With no `handleError` defined, SvelteKit generates
 * `({ error }) => { console.error(error) }`, which returns `undefined`; the
 * client then falls back to `{ message: get_message(error) }`, and
 * `get_message` returns the literal string `'Internal Error'` for anything that
 * is not a `SvelteKitError`. Every error this project defines is a plain
 * `Error` subclass, so EVERY one of them took that branch — and
 * `read/+error.svelte`'s `{page.error?.message}`, the paragraph whose entire
 * job is to say which gate tripped, rendered two words of English in an
 * Arabic-only `dir="rtl"` document, while the typed detail the error carried
 * (which field, expected vs actual) reached only `console.error`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  THE MAPPING IS BY `code`, NOT BY THE ERROR'S OWN MESSAGE
 * ═══════════════════════════════════════════════════════════════════════════
 * `ContentError.message` is deliberately English developer prose — it is what
 * goes in the diagnostics ring buffer and in a bug report, and translating it
 * would make both worse. So the SCREEN gets a sentence written for a reader,
 * chosen by the stable `code` (which `types.ts` says may be added to but never
 * renamed), and the CONSOLE keeps the full typed error.
 *
 * Nothing here is allowed to put Latin script on the screen. The default arm is
 * a generic Arabic sentence, not the error's message, precisely so that an
 * error class nobody has thought about yet cannot leak prose into the UI —
 * `tests/ui/errors.spec.ts` asserts that over every code and over a handful of
 * foreign error shapes.
 *
 * `/repair` and `/about/coverage` are M4. When they land, the version-mismatch
 * arms below should gain a link rather than more prose.
 */

import type { HandleClientError } from '@sveltejs/kit';

import { ContentError, type ContentErrorCode } from '$lib/content';
/* The MODULE, not `$lib/ui/reader`'s barrel: the barrel re-exports five Svelte
 * components, and a hook that pulls the whole reader in to name one error class
 * is a hook that cannot be unit-tested outside a browser build. */
import { ReaderContractError } from '$lib/ui/reader/parts';

/** The one sentence shown when nothing more specific is known. */
export const GENERIC_ERROR_AR = 'حدث خطأ غير متوقع.';

/**
 * One Arabic sentence per failure code. Exhaustive over `ContentErrorCode` by
 * type, so adding a code to the union without adding a sentence is a build
 * error rather than an English string on a reader's screen.
 */
export const CONTENT_ERROR_AR: Record<ContentErrorCode, string> = {
	fetch_failed: 'تعذّر تحميل نص المصحف. تحقّق من اكتمال التنزيل ثم أعد المحاولة.',
	http_error: 'تعذّر تحميل ملف من ملفات المصحف.',
	malformed_json: 'أحد ملفات المصحف تالف ولا يمكن قراءته.',
	malformed_shape: 'أحد ملفات المصحف لا يطابق الشكل المتوقّع.',
	unknown_juz: 'الجزء المطلوب غير موجود في النسخة المثبّتة.',
	unknown_surah: 'السورة المطلوبة غير موجودة في النسخة المثبّتة.',
	unknown_ayah: 'الآية المطلوبة غير موجودة في النسخة المثبّتة.',
	version_mismatch:
		'نسخة نص المصحف المثبّتة لا تطابق نسخة التطبيق؛ لن يُعرض المصحف حتى يُعاد تنزيله، ' +
		'حتى لا تشير علاماتك إلى آيات غير التي اخترتها.',
	corpus_version_mismatch:
		'ملفات المصحف المثبّتة من إصدارين مختلفين؛ لن يُعرض المصحف حتى يُعاد تنزيله كاملًا.',
	word_count_mismatch: 'عدد كلمات إحدى الآيات لا يطابق فهرس التطبيق؛ لن تُعرض حدود الكلمات.',
	tokeniser_disagreement: 'تقسيم إحدى الآيات إلى كلمات لا يطابق فهرس التطبيق.',
	basmala_mismatch: 'موضع البسملة في إحدى الآيات لا يطابق فهرس التطبيق.',
	segmentation_not_lossless: 'تعذّر إعادة تركيب نصّ إحدى الآيات كما هو في المصدر.',
	word_span_out_of_range: 'المدى المطلوب من الكلمات خارج حدود الآية.'
};

export const handleError: HandleClientError = ({ error }) => {
	/* The console keeps the WHOLE typed error — the field, the expected and the
	 * actual — because that is what a bug report needs and the screen is not
	 * where it belongs. */
	console.error(error);

	if (error instanceof ContentError) {
		return { message: CONTENT_ERROR_AR[error.code] ?? GENERIC_ERROR_AR };
	}
	if (error instanceof ReaderContractError) {
		return {
			message: 'تعذّر رسم نص المصحف بشكل مطابق لفهرس الكلمات؛ لن يُعرض المصحف حتى يُصلَح ذلك.'
		};
	}
	return { message: GENERIC_ERROR_AR };
};
